<script setup lang="ts">
import type { NativeDateInput } from '#layers/base/public';
import { useClipboard } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { formatPreciseInstantWithTimeZone, formatTimezoneLabel, formatUnixTimestampSeconds, resolveTimezone, TIMEZONE_LOCAL, TIMEZONE_UTC, toInstant, useUserLocale } from '#layers/base/public';

interface TimestampHoverCardRow {
	id: string;
	icon: string;
	label: string;
	detail?: string;
	value: string;
	mono?: boolean;
}

const props = defineProps<{
	instant: NativeDateInput;
	localTimezone?: string;
}>();

const { formatLocale } = useUserLocale();
const { t } = useI18n();
const appConfig = useAppConfig();
const { copy, copied } = useClipboard();

const copiedId = ref<string>();

function copyRow(row: TimestampHoverCardRow): void {
	copy(row.value);
	copiedId.value = row.id;
}

const instant = computed(() => toInstant(props.instant));
const localTimezone = computed(() => resolveTimezone(props.localTimezone ?? TIMEZONE_LOCAL));
const localDateTime = computed(() => instant.value.toZonedDateTimeISO(localTimezone.value).toPlainDateTime());
const localTimezoneLabel = computed(() => formatTimezoneLabel(localTimezone.value, {
	locale: formatLocale.value,
	dateTime: localDateTime.value,
}));

const rows = computed<TimestampHoverCardRow[]>(() => [
	{
		id: 'local',
		icon: 'lucide:monitor',
		label: t('time.local'),
		detail: localTimezoneLabel.value,
		value: formatPreciseInstantWithTimeZone(instant.value, {
			timeZone: localTimezone.value,
			locale: formatLocale.value,
		}),
	},
	{
		id: 'utc',
		icon: 'lucide:globe-2',
		label: t('time.utc'),
		value: formatPreciseInstantWithTimeZone(instant.value, {
			timeZone: TIMEZONE_UTC,
			locale: formatLocale.value,
		}),
	},
	{
		id: 'unix',
		icon: 'lucide:terminal',
		label: t('time.unix'),
		value: formatUnixTimestampSeconds(instant.value),
		mono: true,
	},
]);
</script>

<template>
	<DPopover
		mode="hover"
		:open-delay="0"
		:close-delay="0"
		:content="{ side: 'top', align: 'center', sideOffset: 4 }"
		:ui="{ content: 'w-96 max-w-[calc(100vw-2rem)] p-0 data-[state=closed]:animate-none data-[state=open]:animate-none' }"
	>
		<slot />

		<template #content>
			<div class="space-y-1 p-3" data-testid="timestamp-hover-card">
				<button
					v-for="row in rows"
					:key="row.id"
					type="button"
					class="
						group grid w-full grid-cols-[auto_minmax(5rem,auto)_minmax(0,1fr)_auto] items-center gap-2 rounded-sm p-1
						text-start transition-colors
						hover:bg-elevated
					"
					:aria-label="`${t('common.copy_to_clipboard')}: ${row.label}, ${row.value}`"
					:data-testid="`timestamp-hover-card-row-${row.id}`"
					@click="copyRow(row)"
				>
					<DIcon :name="row.icon" class="size-4 text-dimmed transition-colors group-hover:text-default" />
					<span class="flex min-w-0 items-baseline gap-1">
						<span class="text-sm font-medium text-default transition-colors group-hover:text-highlighted">{{ row.label }}</span>
						<span v-if="row.detail" class="text-sm text-muted">{{ row.detail }}</span>
					</span>
					<span class="min-w-0 truncate text-end text-sm text-default" :class="{ 'font-mono': row.mono }">
						{{ row.value }}
					</span>
					<DIcon
						:name="copied && copiedId === row.id ? appConfig.ui.icons.check : 'lucide:copy'"
						class="size-4 text-dimmed"
					/>
				</button>
			</div>
		</template>
	</DPopover>
</template>
